'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { Cart } from '@cobrastyle/shared-types';
import { useCustomer } from '@cobrastyle/storefront-core/hooks';
import { buttonClassName } from '@cobrastyle/ui';
import { StoreLink } from '@/components/store';
import { getLicensedCheckoutRequired } from '@/actions/licensed-checkout';
import { LICENSED_CHECKOUT_LOGIN_PATH } from '@/lib/licensed-checkout/route';

/**
 * The cart's way to the checkout, shared by the cart page and the mini cart so the two cannot differ.
 *
 * A guest whose cart holds a licence-required product cannot use the checkout: /kassa sends them to
 * sign in. Customers press the checkout button out of habit, so landing on a sign-in page unexplained
 * reads as a fault. For that guest the button says "Logga in", goes straight there, and a line above
 * it says why.
 *
 * The flag comes from Magento (Cart.requires_licensed_checkout), not from the storefront's own SKU
 * decorations, so it agrees with the rule /kassa and the backend guards use — a suppressor in a
 * licensed category carries no th_licenspliktig and would otherwise be missed. It is asked again
 * whenever the cart's contents change, and only for guests.
 *
 * The cart page passes `initialSignInRequired`, answered during its server render, so the button is
 * right on first paint; asked only after mount it showed "Till kassan" and then flipped to "Logga in".
 * The mini cart has no server render of its own and asks after mount.
 */
export function CheckoutButton({
  cart,
  className,
  initialSignInRequired,
}: {
  cart: Cart | null;
  className?: string;
  initialSignInRequired?: boolean;
}) {
  const t = useTranslations('cart');
  const tLicense = useTranslations('cart.license');
  const { isLoggedIn } = useCustomer();
  const signInRequired = useSignInRequired(cart, isLoggedIn, initialSignInRequired);

  return (
    <>
      {signInRequired && (
        <p className="text-center text-[13px] leading-[1.5] text-accent-700">{tLicense('signInRequired')}</p>
      )}
      <StoreLink
        href={signInRequired ? LICENSED_CHECKOUT_LOGIN_PATH : '/kassa'}
        className={buttonClassName({ size: 'lg', className })}
      >
        {signInRequired ? tLicense('signIn') : t('checkout')}
      </StoreLink>
    </>
  );
}

function useSignInRequired(cart: Cart | null, isLoggedIn: boolean, initial?: boolean): boolean {
  const [required, setRequired] = useState(initial ?? false);

  // What the answer depends on, as one comparable value: the cart and its lines. Null when there is
  // nothing to ask — a signed-in customer or an empty cart.
  const key =
    !isLoggedIn && cart && cart.items.length > 0
      ? `${cart.id}|${cart.items.map((item) => `${item.product.sku}:${item.quantity}`).join(',')}`
      : null;
  // The cart the server's answer was for. Asking again for that same cart on mount would only repeat
  // it; any later change of contents is a different key and is asked.
  const seededKey = useRef(initial === undefined ? undefined : key);

  useEffect(() => {
    if (key !== null && key === seededKey.current) return;
    // Spent once the contents differ: returning to them later is asked like any other change.
    seededKey.current = undefined;
    if (key === null) {
      setRequired(false);
      return;
    }

    let current = true;
    getLicensedCheckoutRequired()
      .then((answer) => {
        if (current) setRequired(answer);
      })
      .catch(() => {
        // A failed action costs only the warning; /kassa still enforces sign-in.
        if (current) setRequired(false);
      });

    return () => {
      current = false;
    };
  }, [key]);

  return required;
}
